import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../../context/InventoryContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { Trash, Plus, X, Package, Calculator } from 'lucide-react';
import SupplyThumbnail from './SupplyThumbnail';
import ImagePreviewModal from '../ui/ImagePreviewModal';

interface BillOfMaterialsItem {
  supply_id: string;
  supply_name: string;
  supply_sku: string;
  quantity: number;
  cost_per_unit: number;
}

interface ManufacturedProduct {
  id: string;
  name: string;
  description: string;
  category_id: string;
  price: number;
  cost_price: number;
  stock_quantity: number;
  min_stock_level: number;
  image: string;
  sku: string;
  location: string;
  status: string;
  bill_of_materials: BillOfMaterialsItem[];
  production_time: number;
  batch_size: number;
}

interface ProductManufacturingFormProps {
  product?: ManufacturedProduct;
  isEdit?: boolean;
}

const ProductManufacturingForm: React.FC<ProductManufacturingFormProps> = ({ 
  product,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const { categories, products: supplies } = useInventory();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: 0,
    stock_quantity: 0,
    min_stock_level: 0,
    image: '',
    sku: '',
    location: '',
    status: 'design',
    production_time: 0,
    batch_size: 1,
  });
  
  const [billOfMaterials, setBillOfMaterials] = useState<BillOfMaterialsItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string; description?: string } | null>(null);

  useEffect(() => {
    if (product && isEdit) {
      setFormData({
        name: product.name,
        description: product.description || '',
        category_id: product.category_id || '',
        price: product.price,
        stock_quantity: product.stock_quantity,
        min_stock_level: product.min_stock_level,
        image: product.image || '',
        sku: product.sku,
        location: product.location || '',
        status: product.status,
        production_time: product.production_time || 0,
        batch_size: product.batch_size || 1,
      });
      setBillOfMaterials(product.bill_of_materials || []);
    } else if (!isEdit) {
      // Generate new product SKU for new products
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setFormData(prev => ({
        ...prev,
        sku: `PROD-${new Date().getFullYear()}-${timestamp}-${random}`
      }));
    }
  }, [product, isEdit]);

  const addBOMItem = () => {
    setBillOfMaterials([...billOfMaterials, {
      supply_id: '',
      supply_name: '',
      supply_sku: '',
      quantity: 1,
      cost_per_unit: 0
    }]);
  };

  const removeBOMItem = (index: number) => {
    setBillOfMaterials(billOfMaterials.filter((_, i) => i !== index));
  };

  const updateBOMItem = (index: number, field: keyof BillOfMaterialsItem, value: string | number) => {
    const newBOM = [...billOfMaterials];
    if (field === 'supply_id' && typeof value === 'string') {
      const supply = supplies.find(s => s.id === value);
      if (supply) {
        newBOM[index] = {
          ...newBOM[index],
          supply_id: value,
          supply_name: supply.name,
          supply_sku: supply.sku,
          cost_per_unit: supply.cost_price
        };
      }
    } else {
      newBOM[index] = {
        ...newBOM[index],
        [field]: value
      };
    }
    setBillOfMaterials(newBOM);
    calculateCostPrice(newBOM);
  };

  const calculateCostPrice = (bom: BillOfMaterialsItem[]) => {
    const totalCost = bom.reduce((sum, item) => sum + (item.quantity * item.cost_per_unit), 0);
    setFormData(prev => ({ ...prev, cost_price: totalCost }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required';
    }
    
    if (formData.price < 0) {
      newErrors.price = 'Price cannot be negative';
    }
    
    if (formData.stock_quantity < 0) {
      newErrors.stock_quantity = 'Stock quantity cannot be negative';
    }
    
    if (formData.min_stock_level < 0) {
      newErrors.min_stock_level = 'Minimum stock level cannot be negative';
    }

    if (formData.production_time < 0) {
      newErrors.production_time = 'Production time cannot be negative';
    }

    if (formData.batch_size <= 0) {
      newErrors.batch_size = 'Batch size must be greater than 0';
    }

    if (billOfMaterials.length === 0) {
      newErrors.bill_of_materials = 'At least one supply component is required';
    }

    if (billOfMaterials.some(item => !item.supply_id)) {
      newErrors.bill_of_materials = 'Please select supplies for all bill of materials items';
    }

    if (billOfMaterials.some(item => item.quantity <= 0)) {
      newErrors.bill_of_materials = 'All quantities must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [name]: numValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const productData = {
        ...formData,
        user_id: user?.id,
        category_id: formData.category_id || null,
        bill_of_materials: billOfMaterials,
        cost_price: billOfMaterials.reduce((sum, item) => sum + (item.quantity * item.cost_per_unit), 0)
      };

      if (isEdit && product) {
        const { error } = await supabase
          .from('manufactured_products')
          .update(productData)
          .eq('id', product.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('manufactured_products')
          .insert([productData]);

        if (error) throw error;
      }
      
      navigate('/products');
    } catch (error: any) {
      console.error('Error saving manufactured product:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to save product. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = () => {
    if (!product) return;
    
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      deleteProduct();
    }
  };

  const deleteProduct = async () => {
    if (!product) return;

    try {
      const { error } = await supabase
        .from('manufactured_products')
        .delete()
        .eq('id', product.id);

      if (error) throw error;
      navigate('/products');
    } catch (error) {
      console.error('Error deleting product:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to delete product. Please try again.'
      }));
    }
  };

  const categoryOptions = [
    { value: '', label: 'Uncategorized' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    }))
  ];

  const statusOptions = [
    { value: 'design', label: 'Design Phase' },
    { value: 'prototype', label: 'Prototype' },
    { value: 'production', label: 'In Production' },
    { value: 'discontinued', label: 'Discontinued' },
  ];

  const supplyOptions = [
    { value: '', label: 'Select Supply' },
    ...supplies.map(supply => ({
      value: supply.id,
      label: `${supply.name} (${supply.sku}) - $${supply.cost_price.toFixed(2)}`
    }))
  ];

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{isEdit ? 'Edit Product' : 'Create New Product'}</CardTitle>
          {isEdit && product && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteProduct}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete Product
            </Button>
          )}
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                fullWidth
                required
              />
              
              <Select
                label="Category"
                options={categoryOptions}
                value={formData.category_id}
                onChange={handleSelectChange('category_id')}
                error={errors.category_id}
                fullWidth
              />
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                placeholder="Detailed description of the manufactured product"
              />
            </div>
          </div>

          {/* Bill of Materials */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Bill of Materials</h3>
              <p className="text-sm text-gray-600 mt-1">
                Changes to the Bill of Materials automatically update supply-product allocations
              </p>
            </div>

            {errors.bill_of_materials && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 p-2 rounded">
                {errors.bill_of_materials}
              </div>
            )}

            <div className="space-y-4">
              {billOfMaterials.map((item, index) => {
                const supply = supplies.find(s => s.id === item.supply_id);

                return (
                  <div key={index} className="flex gap-3 p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      <SupplyThumbnail
                        imageUrl={supply?.image}
                        alt={supply?.name || 'Supply component'}
                        size="md"
                        showZoom={!!supply?.image}
                        onClick={() => {
                          if (supply?.image) {
                            setPreviewImage({
                              url: supply.image,
                              title: supply.name,
                              description: `SKU: ${supply.sku} | Stock: ${supply.stock_quantity}`
                            });
                          }
                        }}
                      />
                    </div>

                    {/* Form Fields */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                      <div className="md:col-span-6">
                        <Select
                          label="Supply"
                          options={supplyOptions}
                          value={item.supply_id}
                          onChange={(value) => updateBOMItem(index, 'supply_id', value)}
                          fullWidth
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          label="Quantity"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateBOMItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          fullWidth
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Input
                          label="Cost per Unit"
                          type="number"
                          step="0.01"
                          value={item.cost_per_unit}
                          onChange={(e) => updateBOMItem(index, 'cost_per_unit', parseFloat(e.target.value) || 0)}
                          fullWidth
                          disabled
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeBOMItem(index)}
                          className="text-red-600 hover:text-red-700"
                          aria-label="Remove supply"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {billOfMaterials.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No supplies added yet</p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addBOMItem}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Supply
                  </Button>
                </div>
              )}

              {billOfMaterials.length > 0 && (
                <>
                  {/* Add Supply Button - positioned above cost summary */}
                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={addBOMItem}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Supply
                    </Button>
                  </div>

                  {/* Total Production Cost */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-sm font-medium text-blue-900">
                          Total Production Cost per Unit:
                        </span>
                      </div>
                      <span className="text-lg font-bold text-blue-900">
                        ${billOfMaterials.reduce((sum, item) => sum + (item.quantity * item.cost_per_unit), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Manufacturing Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Manufacturing Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Production Time (minutes)"
                name="production_time"
                type="number"
                min="0"
                value={formData.production_time.toString()}
                onChange={handleNumberChange}
                error={errors.production_time}
                placeholder="Time to produce one unit"
                fullWidth
              />
              
              <Input
                label="Batch Size"
                name="batch_size"
                type="number"
                min="1"
                value={formData.batch_size.toString()}
                onChange={handleNumberChange}
                error={errors.batch_size}
                placeholder="Units produced per batch"
                fullWidth
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Select
                label="Production Status"
                options={statusOptions}
                value={formData.status}
                onChange={handleSelectChange('status')}
                error={errors.status}
                fullWidth
                required
              />
              
              <Input
                label="Production Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                error={errors.location}
                placeholder="e.g., Manufacturing Floor A"
                fullWidth
              />
            </div>
          </div>

          {/* Inventory Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Inventory Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Current Stock"
                name="stock_quantity"
                type="number"
                min="0"
                value={formData.stock_quantity.toString()}
                onChange={handleNumberChange}
                error={errors.stock_quantity}
                fullWidth
                required
              />
              
              <Input
                label="Minimum Stock Level"
                name="min_stock_level"
                type="number"
                min="0"
                value={formData.min_stock_level.toString()}
                onChange={handleNumberChange}
                error={errors.min_stock_level}
                fullWidth
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                error={errors.sku}
                helperText="Product Stock Keeping Unit - must be unique"
                fullWidth
                required
              />
              
              <Input
                label="Selling Price ($)"
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price.toString()}
                onChange={handleNumberChange}
                error={errors.price}
                fullWidth
                required
              />
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
            <Input
              label="Image URL"
              name="image"
              value={formData.image}
              onChange={handleInputChange}
              error={errors.image}
              fullWidth
            />
          </div>

          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/products')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {isEdit ? 'Update Product' : 'Create Product'}
          </Button>
        </CardFooter>
      </form>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url}
        title={previewImage?.title || ''}
        description={previewImage?.description}
      />
    </Card>
  );
};

export default ProductManufacturingForm;